import React, { useState, useEffect } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import RhPainel from './RhPainel';
import { useCreches } from '../hooks/useCreches';
import { formatCep, formatCpf, formatPhone, isValidCep, isValidCpf, isValidEmail, isValidPhone, normalizeCep } from '../common/validators';
import api, { getCurrentTenantId } from '../services/api';
import { useFuncionarios } from '../hooks/useFuncionarios';

interface Cargo {
  id: number;
  nome: string;
  descricao?: string;
  customizado: boolean;
}

interface Funcionario {
  id: number;
  codigo_funcionario: string;
  creche_id: number;
  nome: string;
  cpf: string;
  rg: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  endereco: string;
  telefone: string;
  whatsapp: string;
  email: string;
  id_cargo: number;
  formacao: string;
  certificacoes: string;
  carteira_trabalho_numero?: string;
  carteira_trabalho_serie?: string;
  pis?: string;
  data_admissao: string;
  ativo: boolean;
}

interface Ponto { id: number; creche_id: number; funcionario_id: number; entrada: string; saida: string; pausas: string; }
interface Escala { id: number; creche_id: number; funcionario_id: number; tipo: string; horario: string; substituicao: string; }
interface Capacitacao { id: number; creche_id: number; funcionario_id: number; curso: string; carga_horaria: number; data_conclusao: string; }

export default function FuncionariosPage() {
  const { options } = useCreches();
  const tenantScoped = Boolean(getCurrentTenantId());
  const { funcionarios } = useFuncionarios();
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [novoCargoNome, setNovoCargoNome] = useState('');
  const [novoCargoDesc, setNovoCargoDesc] = useState('');
  const [carregandoCargos, setCarregandoCargos] = useState(false);
  const [expandirCargos, setExpandirCargos] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  useEffect(() => {
    carregarCargos();
  }, []);

  const carregarCargos = async () => {
    setCarregandoCargos(true);
    try {
      const { data } = await api.get<Cargo[]>('/funcionarios/cargos');
      setCargos(data);
    } catch (erro) {
      console.error('Erro ao carregar cargos:', erro);
    } finally {
      setCarregandoCargos(false);
    }
  };

  const adicionarCargo = async () => {
    if (!novoCargoNome.trim()) {
      alert('Nome do cargo é obrigatório');
      return;
    }

    try {
      const { data: novoCargo } = await api.post<Cargo>('/funcionarios/cargos', {
        nome: novoCargoNome.trim(),
        descricao: novoCargoDesc.trim(),
      });
      setCargos([...cargos, novoCargo]);
      setNovoCargoNome('');
      setNovoCargoDesc('');
    } catch (erro) {
      console.error('Erro ao criar cargo:', erro);
      alert('Erro ao criar cargo. Verifique se já existe um cargo com esse nome.');
    }
  };

  const deletarCargo = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este cargo?')) {
      return;
    }

    try {
      await api.delete(`/funcionarios/cargos/${id}`);
      setCargos(cargos.filter((c) => c.id !== id));
    } catch (erro) {
      console.error('Erro ao deletar cargo:', erro);
      alert('Erro ao deletar cargo. Verifique se há funcionários com esse cargo.');
    }
  };

  function montarEndereco(item: Partial<Funcionario>) {
    const partes = [
      item.logradouro?.trim(),
      item.numero?.trim() ? `nº ${item.numero.trim()}` : '',
      item.complemento?.trim(),
      item.bairro?.trim(),
      item.cidade?.trim(),
      item.estado?.trim(),
      item.cep?.trim() ? `CEP ${item.cep.trim()}` : '',
    ].filter(Boolean);

    return partes.join(', ');
  }

  async function buscarCep(item: Partial<Funcionario>, onChange: (campo: keyof Funcionario, valor: any) => void) {
    const cep = normalizeCep(item.cep || '');
    if (cep.length !== 8) return;

    setBuscandoCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        window.alert('CEP não encontrado.');
        return;
      }

      onChange('logradouro', data.logradouro || '');
      onChange('bairro', data.bairro || '');
      onChange('cidade', data.localidade || '');
      onChange('estado', data.uf || '');

      const atualizado = {
        ...item,
        cep: formatCep(cep),
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
      };
      onChange('endereco', montarEndereco(atualizado));
    } catch {
      window.alert('Não foi possível consultar o CEP no momento.');
    } finally {
      setBuscandoCep(false);
    }
  }

  function atualizarCampoEndereco(
    item: Partial<Funcionario>,
    onChange: (campo: keyof Funcionario, valor: any) => void,
    campo: keyof Funcionario,
    valor: any,
  ) {
    const atualizado = { ...item, [campo]: valor };
    onChange(campo, valor);
    onChange('endereco', montarEndereco(atualizado));
  }

  return (
    <div className="stack-cadastros">
      {/* RH: ponto, escalas, treinamentos e avaliações */}
      <RhPainel />

      {/* GERENCIADOR DE CARGOS */}
      <div className="secao-cargos" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', background: '#f9f9f9' }}>
        <h3 style={{ marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setExpandirCargos(!expandirCargos)}>
          🏷️ Gestão de Cargos {expandirCargos ? '▼' : '▶'}
        </h3>

        {expandirCargos && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                Cargos padrão (não são deletáveis). Cargos customizados criados pela sua gestão aparecem com 🔧.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Nome do novo cargo"
                value={novoCargoNome}
                onChange={(e) => setNovoCargoNome(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && adicionarCargo()}
              />
              <input
                type="text"
                placeholder="Descrição (opcional)"
                value={novoCargoDesc}
                onChange={(e) => setNovoCargoDesc(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && adicionarCargo()}
              />
              <button onClick={adicionarCargo} disabled={carregandoCargos} style={{ padding: '0.5rem 1rem' }}>
                + Adicionar
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
              {cargos.map((cargo) => (
                <div
                  key={cargo.id}
                  style={{
                    padding: '0.75rem',
                    border: `1px solid ${cargo.customizado ? '#f39c12' : '#bdc3c7'}`,
                    borderRadius: '4px',
                    background: cargo.customizado ? '#fff9e6' : '#ecf0f1',
                    fontSize: '0.9rem',
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    {cargo.customizado && '🔧 '}
                    {cargo.nome}
                  </div>
                  {cargo.descricao && <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>{cargo.descricao}</div>}
                  {cargo.customizado && (
                    <button
                      onClick={() => deletarCargo(cargo.id)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                      }}
                    >
                      Deletar
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* CADASTRO DE FUNCIONÁRIOS */}
      <TabelaCrud<Funcionario>
        titulo="Funcionários — Cadastro Completo"
        endpoint="/funcionarios"
        colunas={[
          { chave: 'codigo_funcionario', label: 'Código' },
          { chave: 'nome', label: 'Nome' },
          { chave: 'id_cargo', label: 'Cargo/Função', render: (item) => cargos.find((cargo) => cargo.id === item.id_cargo)?.nome || 'Não informado' },
          { chave: 'email', label: 'E-mail' },
          { chave: 'data_admissao', label: 'Admissão' },
        ]}
        itemVazio={{
          id: 0,
          codigo_funcionario: '',
          creche_id: 0,
          nome: '',
          cpf: '',
          rg: '',
          cep: '',
          logradouro: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: '',
          endereco: '',
          telefone: '',
          whatsapp: '',
          email: '',
          id_cargo: 0,
          formacao: '',
          certificacoes: '',
          carteira_trabalho_numero: '',
          carteira_trabalho_serie: '',
          pis: '',
          data_admissao: new Date().toISOString().split('T')[0],
          ativo: true,
        }}
        validateItem={(i) => {
          if (!tenantScoped && !i.creche_id) return 'Selecione a creche.';
          if (!i.nome?.trim()) return 'Nome obrigatório.';
          if (!isValidCpf(i.cpf || '')) return 'CPF inválido.';
          if (!isValidCep(i.cep || '')) return 'CEP inválido.';
          if (!i.logradouro?.trim()) return 'Logradouro obrigatório.';
          if (!i.numero?.trim()) return 'Número do endereço obrigatório.';
          if (!i.bairro?.trim()) return 'Bairro obrigatório.';
          if (!i.cidade?.trim()) return 'Cidade obrigatória.';
          if (!i.estado?.trim()) return 'UF obrigatória.';
          if (!isValidPhone(i.telefone || '', 'mobile')) return 'Telefone inválido.';
          if (!isValidPhone(i.whatsapp || '', 'mobile')) return 'WhatsApp inválido.';
          if (!isValidEmail(i.email || '')) return 'E-mail inválido.';
          if (!i.id_cargo) return 'Cargo/Função obrigatório.';
          if (!i.formacao?.trim()) return 'Formação acadêmica é obrigatória.';
          if (!i.data_admissao) return 'Data de admissão é obrigatória.';
          if (!montarEndereco(i)) return 'Endereço do funcionário precisa ser preenchido.';
          return null;
        }}
        renderFormulario={(i, onChange) => (
          <>
            {i.codigo_funcionario && (
              <div style={{ padding: '0.75rem', background: '#ecf0f1', borderRadius: '4px', marginBottom: '1rem' }}>
                <strong>Código: {i.codigo_funcionario}</strong> (gerado automaticamente)
              </div>
            )}

            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>
                Creche
                <select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Nome
                <input value={i.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} />
              </label>
            </div>

            <div className="linha-campos linha-3">
              <label>
                CPF
                <input value={i.cpf ?? ''} onChange={(e) => onChange('cpf', formatCpf(e.target.value))} placeholder="000.000.000-00" />
              </label>
              <label>
                RG
                <input value={i.rg ?? ''} onChange={(e) => onChange('rg', e.target.value)} />
              </label>
              <label>
                Email
                <input type="email" value={i.email ?? ''} onChange={(e) => onChange('email', e.target.value)} />
              </label>
            </div>

            <div style={{ marginTop: '0.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ marginBottom: '0.75rem' }}>📍 Endereço via CEP</h4>

              <div className="linha-campos linha-2">
                <label>
                  CEP
                  <input
                    value={i.cep ?? ''}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'cep', formatCep(e.target.value))}
                    onBlur={() => buscarCep(i, onChange)}
                    placeholder="00000-000"
                  />
                </label>
                <label>
                  {buscandoCep ? 'Buscando endereço...' : 'Logradouro'}
                  <input
                    value={i.logradouro ?? ''}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'logradouro', e.target.value)}
                  />
                </label>
              </div>

              <div className="linha-campos linha-3">
                <label>
                  Número
                  <input
                    value={i.numero ?? ''}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'numero', e.target.value)}
                  />
                </label>
                <label>
                  Complemento
                  <input
                    value={i.complemento ?? ''}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'complemento', e.target.value)}
                    placeholder="Opcional"
                  />
                </label>
                <label>
                  Bairro
                  <input
                    value={i.bairro ?? ''}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'bairro', e.target.value)}
                  />
                </label>
              </div>

              <div className="linha-campos linha-2">
                <label>
                  Cidade
                  <input
                    value={i.cidade ?? ''}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'cidade', e.target.value)}
                  />
                </label>
                <label>
                  UF
                  <input
                    value={i.estado ?? ''}
                    maxLength={2}
                    onChange={(e) => atualizarCampoEndereco(i, onChange, 'estado', e.target.value.toUpperCase())}
                    placeholder="SP"
                  />
                </label>
              </div>

              <label>
                Endereço completo
                <input value={i.endereco ?? ''} readOnly />
              </label>
            </div>

            <div className="linha-campos linha-3">
              <label>
                Telefone
                <input value={i.telefone ?? ''} onChange={(e) => onChange('telefone', formatPhone(e.target.value))} placeholder="(00) 00000-0000" />
              </label>
              <label>
                WhatsApp
                <input value={i.whatsapp ?? ''} onChange={(e) => onChange('whatsapp', formatPhone(e.target.value))} placeholder="(00) 00000-0000" />
              </label>
              <label>
                Cargo/Função *
                <select value={i.id_cargo ?? 0} onChange={(e) => onChange('id_cargo', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {cargos.map((cargo) => (
                    <option key={cargo.id} value={cargo.id}>
                      {cargo.nome}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="linha-campos linha-2">
              <label>
                Formação Acadêmica
                <input
                  value={i.formacao ?? ''}
                  onChange={(e) => onChange('formacao', e.target.value)}
                  placeholder="Ex: Pedagogia, Educação Física..."
                />
              </label>
              <label>
                Data de Admissão
                <input
                  type="date"
                  value={i.data_admissao ?? ''}
                  onChange={(e) => onChange('data_admissao', e.target.value)}
                />
              </label>
            </div>

            <label>
              Certificações
              <input
                value={i.certificacoes ?? ''}
                onChange={(e) => onChange('certificacoes', e.target.value)}
                placeholder="Ex: Primeiros Socorros, NR-10, Saúde e Segurança... (opcional)"
              />
            </label>

            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>📋 Documentação Trabalhista</h4>

              <div className="linha-campos linha-2">
                <label>
                  Nº Carteira de Trabalho
                  <input
                    value={i.carteira_trabalho_numero ?? ''}
                    onChange={(e) => onChange('carteira_trabalho_numero', e.target.value)}
                    placeholder="Número"
                  />
                </label>
                <label>
                  Série da Carteira
                  <input
                    value={i.carteira_trabalho_serie ?? ''}
                    onChange={(e) => onChange('carteira_trabalho_serie', e.target.value)}
                    placeholder="Série"
                  />
                </label>
              </div>

              <label>
                Nº PIS
                <input
                  value={i.pis ?? ''}
                  onChange={(e) => onChange('pis', e.target.value)}
                  placeholder="PIS/Pasep"
                />
              </label>
            </div>
          </>
        )}
      />

      <TabelaCrud<Ponto>
        titulo="Funcionários — Controle de Ponto"
        endpoint="/funcionarios/ponto"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'funcionario_id', label: 'Funcionário', render: (item) => funcionarios.find((f) => f.id === item.funcionario_id)?.nome || `ID ${item.funcionario_id}` }, { chave: 'entrada', label: 'Entrada' }, { chave: 'saida', label: 'Saída' }]}
        itemVazio={{ creche_id: 0, funcionario_id: 0, entrada: '', saida: '', pausas: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.funcionario_id || !i.entrada || !i.saida ? 'Preencha funcionário, entrada e saída.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Funcionário<select value={i.funcionario_id ?? 0} onChange={(e) => onChange('funcionario_id', Number(e.target.value))}><option value={0}>Selecione...</option>{funcionarios.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}</select></label>
            </div>
            <div className="linha-campos linha-3">
              <label>Entrada<input type="time" value={i.entrada ?? ''} onChange={(e) => onChange('entrada', e.target.value)} /></label>
              <label>Saída<input type="time" value={i.saida ?? ''} onChange={(e) => onChange('saida', e.target.value)} /></label>
              <label>Pausas<input value={i.pausas ?? ''} onChange={(e) => onChange('pausas', e.target.value)} /></label>
            </div>
          </>
        )}
      />

      <TabelaCrud<Escala>
        titulo="Funcionários — Escalas de Trabalho"
        endpoint="/funcionarios/escalas"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'funcionario_id', label: 'Funcionário', render: (item) => funcionarios.find((f) => f.id === item.funcionario_id)?.nome || `ID ${item.funcionario_id}` }, { chave: 'tipo', label: 'Tipo' }, { chave: 'horario', label: 'Horário' }]}
        itemVazio={{ creche_id: 0, funcionario_id: 0, tipo: 'fixo', horario: '', substituicao: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.funcionario_id || !i.horario ? 'Preencha funcionário e horário.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-3">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Funcionário<select value={i.funcionario_id ?? 0} onChange={(e) => onChange('funcionario_id', Number(e.target.value))}><option value={0}>Selecione...</option>{funcionarios.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}</select></label>
              <label>Tipo<select value={i.tipo ?? 'fixo'} onChange={(e) => onChange('tipo', e.target.value)}><option value="fixo">Fixo</option><option value="plantao">Plantão</option></select></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Horário<input value={i.horario ?? ''} onChange={(e) => onChange('horario', e.target.value)} /></label>
              <label>Substituição<input value={i.substituicao ?? ''} onChange={(e) => onChange('substituicao', e.target.value)} /></label>
            </div>
          </>
        )}
      />

      <TabelaCrud<Capacitacao>
        titulo="Funcionários — Capacitações"
        endpoint="/funcionarios/capacitacoes"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'funcionario_id', label: 'Funcionário', render: (item) => funcionarios.find((f) => f.id === item.funcionario_id)?.nome || `ID ${item.funcionario_id}` }, { chave: 'curso', label: 'Curso' }, { chave: 'data_conclusao', label: 'Conclusão' }]}
        itemVazio={{ creche_id: 0, funcionario_id: 0, curso: '', carga_horaria: 0, data_conclusao: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.funcionario_id || !i.curso || !i.data_conclusao ? 'Preencha os campos obrigatórios da capacitação.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Funcionário<select value={i.funcionario_id ?? 0} onChange={(e) => onChange('funcionario_id', Number(e.target.value))}><option value={0}>Selecione...</option>{funcionarios.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}</select></label>
            </div>
            <div className="linha-campos linha-3">
              <label>Curso<input value={i.curso ?? ''} onChange={(e) => onChange('curso', e.target.value)} /></label>
              <label>Carga horária<input type="number" value={i.carga_horaria ?? 0} onChange={(e) => onChange('carga_horaria', Number(e.target.value))} /></label>
              <label>Data conclusão<input type="date" value={i.data_conclusao ?? ''} onChange={(e) => onChange('data_conclusao', e.target.value)} /></label>
            </div>
          </>
        )}
      />
    </div>
  );
}

